import {
  ChangeEvent,
  FocusEvent,
  FunctionComponent,
  useEffect,
  useState,
} from 'react'
import { useHistory } from 'react-router'
import { Grid } from '@material-ui/core'
import { MdCheck } from 'react-icons/md'

import IVincularCNPJRequest from 'modules/user/domain/entities/VincularCNPJRequest'
import { IConsultZipCode } from 'modules/user/domain/usecases/ConsultZipCodeImpl'
import { IValidateAntt } from 'modules/user/domain/usecases/ValidateAnttImpl'
import { IValidateCnpj } from 'modules/user/domain/usecases/ValidateCnpjImpl'
import { IValidateEmail } from 'modules/user/domain/usecases/ValidateEmailImpl'
import { IVerifyCnpjAvailability } from 'modules/user/domain/usecases/VerifyCnpjAvailabilityImpl'
import { IVincularCNPJ } from 'modules/user/domain/usecases/VincularCnpjImpl'
import Button from 'shared/components/Button'
import ContainerPage from 'shared/components/ContainerPageNew'
import InputText from 'shared/components/InputText'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import Modal from 'shared/components/Modal'
import SnackBar from 'shared/components/SnackBar'
import Text from 'shared/components/Text'
import { colors } from 'shared/styles/colors'
import {
  cepMask,
  cnpjMask,
  emailBrRegExp,
  normalizeCnpj,
  phoneMask,
} from 'shared/utils/formatters'
import IValidateAnttResponse from 'modules/user/domain/entities/IValidateAnttResponse'

import { Field, StyledCheck, Title } from './styles'

export interface IProps {
  consultZipCode: IConsultZipCode
  validateCnpj: IValidateCnpj
  validateAntt: IValidateAntt
  validateEmail: IValidateEmail
  verifyCnpjAvailability: IVerifyCnpjAvailability
  vincularCnpj: IVincularCNPJ
}

const VincularCnpj: FunctionComponent<IProps> = ({
  consultZipCode,
  validateCnpj,
  validateAntt,
  validateEmail,
  verifyCnpjAvailability,
  vincularCnpj,
}) => {
  const history = useHistory()
  const [formOk, setFormOk] = useState(false)
  const [cnpj, setCnpj] = useState('')
  const [antt, setAntt] = useState('')
  const [cep, setCep] = useState('')
  const [logradouro, setLogradouro] = useState('')
  const [numero, setNumero] = useState('')
  const [complemento, setComplemento] = useState('')
  const [bairro, setBairro] = useState('')
  const [cidade, setCidade] = useState('')
  const [cidadeIbge, setCidadeIbge] = useState('')
  const [estado, setEstado] = useState('')
  const [telefone, setTelefone] = useState('')
  const [email, setEmail] = useState('')

  const [errorMessage, setErrorMessage] = useState('')
  const [showError, setShowError] = useState(false)
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)
  const [showModalSucesso, setShowModalSucesso] = useState(false)

  useEffect(() => {
    setFormOk(
      Boolean(
        cnpj &&
          antt &&
          cep &&
          logradouro &&
          numero &&
          bairro &&
          cidade &&
          estado &&
          telefone &&
          email,
      ),
    )
  }, [
    antt,
    bairro,
    cep,
    cidade,
    cnpj,
    email,
    estado,
    logradouro,
    numero,
    telefone,
  ])

  const consultCep = async (e: FocusEvent<HTMLInputElement>) => {
    setLogradouro('')
    setBairro('')
    setCidade('')
    setEstado('')
    setCidadeIbge('')
    try {
      if (!e.target.value) return
      const { logradouro, bairro, cidade, cidade_ibge, estado } =
        await consultZipCode.execute({ cep: e.target.value })

      if (cidade) {
        setLogradouro(logradouro)
        setBairro(bairro)
        setCidade(cidade)
        setEstado(estado)
        setCidadeIbge(cidade_ibge)
      } else {
        setErrorMessage('Cep inválido')
        setShowError(true)
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message)
      } else {
        setErrorMessage('Problemas ao consultar o CEP')
      }
      setShowError(true)
    }
  }

  const validaCnpj = async (cnpj: string) => {
    if (!cnpj) return
    const params = {
      type: 'cnpj',
      cnpj,
    }
    await validateCnpj.execute(params)

    const paramsCnpjAvailability = {
      cnpj,
    }

    await verifyCnpjAvailability.execute(paramsCnpjAvailability)
  }

  const validaAntt = async (
    antt: string,
    cnpj: string,
  ): Promise<IValidateAnttResponse> => {
    if (!antt) return
    return await validateAntt.execute({ antt, cnpj })
  }

  const validaEmail = async (email: string) => {
    if (!email) return

    const emailValido = emailBrRegExp.test(email)
    if (!emailValido) throw new Error('E-mail inválido!')

    await validateEmail.execute(email)
  }

  const vincularNovoCnpj = async () => {
    try {
      setLoadingBackdrop(true)
      const normalizedCnpj = normalizeCnpj(cnpj)
      const dadosVinculoCnpj: IVincularCNPJRequest = {
        antt: antt,
        cnpj: normalizedCnpj,
        email: email,
        telefone: telefone,
        endereco: {
          bairro: bairro,
          cep: cep,
          cidade: cidade,
          cidade_ibge: cidadeIbge,
          complemento: complemento,
          estado: estado,
          logradouro: logradouro,
          numero: numero,
        },
      }
      await validaEmail(email)
      await validaCnpj(normalizedCnpj)
      const responseAntt = await validaAntt(antt, normalizedCnpj)
      if (!responseAntt?.aptoTransporte) {
        setShowError(true)
        setErrorMessage(`ANTT: ${responseAntt.mensagem}`)
        return
      }
      await vincularCnpj.execute(dadosVinculoCnpj)
      setShowModalSucesso(true)
    } catch (error: unknown) {
      setShowError(true)
      if (error instanceof Error) {
        setErrorMessage(error.message)
      }
    } finally {
      setLoadingBackdrop(false)
    }
  }

  return (
    <ContainerPage
      breadCrumb="Home / Gerenciar Conta / CNPJs vinculados / Vincular CNPJ"
      titleNextButton="Salvar"
      colorNextButton={formOk ? colors.green : colors.grayLight}
      isNextButtonDisabled={!formOk}
      onClickNextButton={vincularNovoCnpj}
      titleBackButton="Voltar"
      onClickBackButton={() => history.goBack()}
      iconButtonNext={MdCheck}
      title="Dados da empresa"
      titleHeader="Novo CNPJ"
    >
      <Grid container spacing={3}>
        <Grid item md={4}>
          <Field>CNPJ</Field>
          <InputText
            placeholder="Informe o CNPJ"
            name="cnpj"
            type="text"
            value={cnpj}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setCnpj(cnpjMask(e.target.value))
            }
            maxLength={18}
          />
        </Grid>
        <Grid item md={4}>
          <Field>ANTT</Field>
          <InputText
            placeholder="Informe a ANTT"
            name="antt"
            value={antt}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setAntt(e.target.value)
            }
            maxLength={14}
          />
        </Grid>
      </Grid>

      <Title>Dados de contato</Title>

      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Field>CEP</Field>
          <InputText
            onBlur={consultCep}
            placeholder="Informe o CEP"
            name="cep"
            type="text"
            value={cep}
            maxLength={9}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setCep(cepMask(e.target.value))
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Field>Logradouro</Field>
          <InputText
            placeholder="logradouro"
            name="logradouro"
            type="text"
            value={logradouro}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setLogradouro(e.target.value)
            }
          />
        </Grid>
        <Grid item xs={1}>
          <Field>Número</Field>
          <InputText
            style={{ width: 80 }}
            placeholder="Nº"
            name="numero"
            type="text"
            value={numero}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNumero(e.target.value)
            }
          />
        </Grid>
        <Grid item xs={2}>
          <Field>Complemento</Field>
          <InputText
            placeholder="Informe o complemento"
            name="complemento"
            type="text"
            value={complemento}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setComplemento(e.target.value)
            }
          />
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={3}>
          <Field>Bairro</Field>
          <InputText
            style={{ marginTop: 0 }}
            placeholder="Bairro"
            name="cnpj"
            type="text"
            maxLength={18}
            value={bairro}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setBairro(e.target.value)
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Field>Cidade</Field>
          <InputText
            style={{ marginTop: 0 }}
            placeholder="Cidade"
            name="cidade"
            type="text"
            disabled
            value={cidade}
          />
        </Grid>
        <Grid item xs={3}>
          <Field>Estado</Field>
          <InputText
            style={{ width: 80 }}
            placeholder="UF"
            name="uf"
            type="text"
            disabled
            value={estado}
          />
        </Grid>
        <Grid item xs={3} />
        <Grid item xs={3}>
          <Field>Telefone</Field>
          <InputText
            style={{ marginTop: 0 }}
            placeholder="Informe o telefone"
            name="cnpj"
            type="text"
            maxLength={15}
            value={telefone}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setTelefone(phoneMask(e.target.value))
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Field>Email</Field>
          <InputText
            style={{ marginTop: 0 }}
            placeholder="Informe o e-mail do contato"
            name="cidade"
            type="text"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
          />
        </Grid>
      </Grid>
      <SnackBar
        open={showError}
        setOpen={setShowError}
        severity="error"
        message={errorMessage}
        horizontal="center"
      />
      {loadingBackdrop ? (
        <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
      ) : (
        <SnackBar
          duration={15000}
          open={showError}
          message={errorMessage}
          setOpen={setShowError}
          severity={'error'}
        />
      )}
      <Modal
        openModal={showModalSucesso}
        size="small"
        showCloseButton={false}
        onCloseModal={() => setShowModalSucesso(false)}
      >
        <StyledCheck style={{ marginBottom: 20, marginTop: 40 }} />
        <Text
          style={{ fontSize: 20, marginBottom: 20, fontWeight: 'bold' }}
          color={colors.blue}
        >
          CNPJ vinculado com sucesso!
        </Text>
        <Button
          color={colors.green}
          onClick={() => {
            history.push('/cnpjs-vinculados')
            setShowModalSucesso(false)
          }}
        >
          <MdCheck />
          Ok
        </Button>
      </Modal>
    </ContainerPage>
  )
}

export default VincularCnpj
